import { useState } from 'react';
import { useCreateForm, type Form } from '../../api/forms.api';
import type { FormPathType } from '@engage/types';
import { Btn, Icon, ICONS, PathChip } from '../../features/forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../features/forms/ui/tokens';

/**
 * Create-a-form modal. Path-type selection is the heart of this surface —
 * recruiters pick wrong if it's unclear. Each card states explicitly:
 * who it's sent to, what it writes back to, and whether it's public.
 */
export function NewFormModal(props: { onClose: () => void; onCreated: (form: Form) => void }) {
  const [name, setName] = useState('');
  const [pathType, setPathType] = useState<FormPathType>('CANDIDATE');
  const [error, setError] = useState<string | null>(null);
  const mutation = useCreateForm();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      const form = await mutation.mutateAsync({ name: name.trim(), path_type: pathType });
      props.onCreated(form);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create form');
    }
  };

  return (
    <div
      onClick={props.onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,24,60,.32)',
        backdropFilter: 'blur(1px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '60px 24px',
        zIndex: 200,
        fontFamily: FORM_UI_FONT,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(15,18,60,.28)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '20px 24px 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <h2 style={{ fontSize: 19, fontWeight: 800, color: C.indigo, margin: 0 }}>
              Create a form
            </h2>
            <div style={{ fontSize: 12.5, color: C.sub, marginTop: 3 }}>
              Path type is locked after creation, so choose carefully.
            </div>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: C.faint,
            }}
            aria-label="Close"
          >
            <Icon d={ICONS.x} size={20} color={C.faint} />
          </button>
        </div>

        <form onSubmit={submit} style={{ padding: '22px 24px 24px' }}>
          <label
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: C.indigoSoft,
              letterSpacing: 0.3,
            }}
          >
            FORM NAME
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Update your details: DevOps Q2"
            autoFocus
            style={{
              width: '100%',
              marginTop: 6,
              padding: '11px 13px',
              border: `1px solid ${C.line}`,
              borderRadius: 9,
              fontFamily: FORM_UI_FONT,
              fontSize: 13.5,
              color: C.ink,
              boxSizing: 'border-box',
            }}
          />

          <label
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: C.indigoSoft,
              display: 'block',
              margin: '20px 0 8px',
              letterSpacing: 0.3,
            }}
          >
            PATH TYPE
          </label>
          <div style={{ display: 'flex', gap: 12 }}>
            <PathCard
              type="CANDIDATE"
              selected={pathType === 'CANDIDATE'}
              onSelect={() => setPathType('CANDIDATE')}
              title="Candidate"
              body="Tokenised link sent to a known candidate. Pre-filled from their JobTetris profile; updates write back to that candidate record. Never public."
            />
            <PathCard
              type="CONTACT"
              selected={pathType === 'CONTACT'}
              onSelect={() => setPathType('CONTACT')}
              title="Contact"
              body="Open public form for website embeds, gated content and registrations. Creates a new contact or appends to an existing one, matched by email."
            />
          </div>

          {error && (
            <div
              style={{
                marginTop: 16,
                padding: '10px 12px',
                background: C.dangerBg,
                color: C.danger,
                borderRadius: 9,
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
              marginTop: 24,
            }}
          >
            <Btn kind="ghost" onClick={props.onClose}>
              Cancel
            </Btn>
            <Btn kind="primary" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create & open builder'}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  );
}

function PathCard(props: {
  type: FormPathType;
  selected: boolean;
  onSelect: () => void;
  title: string;
  body: string;
}) {
  const col = props.type === 'CANDIDATE' ? C.candidate : C.contact;
  const bg = props.type === 'CANDIDATE' ? C.candidateBg : C.contactBg;
  return (
    <button
      type="button"
      onClick={props.onSelect}
      style={{
        flex: 1,
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: FORM_UI_FONT,
        background: props.selected ? bg : '#fff',
        borderRadius: 12,
        padding: '15px 15px',
        border: `2px solid ${props.selected ? col : C.line}`,
        transition: 'all .15s',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <PathChip type={props.type} size="lg" />
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: 99,
            border: `2px solid ${props.selected ? col : C.line}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {props.selected && (
            <span style={{ width: 9, height: 9, borderRadius: 99, background: col }} />
          )}
        </span>
      </div>
      <div style={{ fontWeight: 700, fontSize: 14, color: C.indigo, marginBottom: 5 }}>
        {props.title}
      </div>
      <div style={{ fontSize: 11.8, color: C.sub, lineHeight: 1.5 }}>{props.body}</div>
    </button>
  );
}
